/**
 * Engine modules with typed dependencies (architecture §2.1, review S-2).
 *
 * A module declares the modules it depends on by *reference*, and its services
 * are whatever its `init` returns. That gives two compile-time guarantees:
 * - `ctx.deps` only contains declared dependencies, so reaching for an
 *   undeclared module is a type error rather than a runtime lookup failure.
 * - A dependency cycle can't be written, because a module must be declared
 *   before another module can reference it.
 *
 * This file is pure types plus ordering. Booting lives in Engine.ts.
 */
import type { ChangeScheduler } from "./ChangeScheduler";
import type { Clock } from "./Clock";
import type { Log } from "./Log";

/** Engine-owned primitives available to every module, even before Core initializes. */
export interface EngineKernel {
  readonly clock: Clock;
  readonly scheduler: ChangeScheduler;
  readonly log: Log;
}

export interface ModuleContext<Deps, Options> {
  /** Services of the declared dependencies, keyed by module name. */
  readonly deps: Deps;
  /** Options passed to `engine.configure(module, …)`, or the module's defaults. */
  readonly options: Options;
  readonly kernel: EngineKernel;
}

export interface EngineModule<
  Name extends string,
  Deps extends readonly AnyEngineModule[],
  Services,
  Options,
> {
  readonly name: Name;
  readonly dependsOn: Deps;
  /** An optional module's init failure is logged as a warning instead of failing boot. */
  readonly optional?: boolean;
  /** Default options, used when the host doesn't call `engine.configure`. */
  readonly options?: () => Options;
  init(ctx: ModuleContext<DepsServices<Deps>, Options>): Services | Promise<Services>;
  shutdown?(services: Services): void | Promise<void>;
}

// `any` is required here: this is the existential "some module" type used in constraints.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyEngineModule = EngineModule<string, readonly any[], any, any>;

/** The services a module's `init` resolves to. */
export type ServicesOf<M> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  M extends EngineModule<string, readonly any[], infer S, any> ? S : never;

/** The options type a module accepts in `engine.configure`. */
export type OptionsOf<M> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  M extends EngineModule<string, readonly any[], any, infer O> ? O : never;

/** Maps a tuple of dependency modules to `{ [name]: services }`. */
export type DepsServices<Deps extends readonly AnyEngineModule[]> = {
  readonly [M in Deps[number] as M["name"]]: ServicesOf<M>;
};

/**
 * Declares a module. This is an identity function whose only job is inference:
 * `const` type parameters keep the name and dependency tuple literal, so
 * `ctx.deps` is precisely typed.
 */
export function defineModule<
  const Name extends string,
  const Deps extends readonly AnyEngineModule[],
  Services,
  Options = undefined,
>(
  definition: EngineModule<Name, Deps, Services, Options>,
): EngineModule<Name, Deps, Services, Options> {
  return definition;
}

/**
 * Orders modules so every module comes after its dependencies (depth-first,
 * post-order). The order is stable: ties keep declaration order. Transitive
 * dependencies that weren't listed explicitly are pulled in.
 */
export function sortModules(modules: readonly AnyEngineModule[]): AnyEngineModule[] {
  const ordered: AnyEngineModule[] = [];
  const state = new Map<AnyEngineModule, "visiting" | "done">();
  const byName = new Map<string, AnyEngineModule>();

  const visit = (module: AnyEngineModule, path: readonly string[]): void => {
    assertUniqueName(byName, module);

    const status = state.get(module);
    if (status === "done") return;
    if (status === "visiting") {
      throw new Error(`Module dependency cycle: ${[...path, module.name].join(" → ")}`);
    }

    state.set(module, "visiting");
    for (const dependency of module.dependsOn as readonly AnyEngineModule[]) {
      visit(dependency, [...path, module.name]);
    }
    state.set(module, "done");
    ordered.push(module);
  };

  for (const module of modules) visit(module, []);
  return ordered;
}

function assertUniqueName(byName: Map<string, AnyEngineModule>, module: AnyEngineModule): void {
  const existing = byName.get(module.name);
  if (existing && existing !== module) {
    throw new Error(`Duplicate module name '${module.name}'`);
  }
  byName.set(module.name, module);
}
